import types from '../mutation-types';
import FunnelAPI from '../../api/funnel';

const pendingStageStatsRequests = {};

const state = {
  records: [],
  selectedFunnel: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
  },
  stageStats: {}, // Novo: stats agregados por funnelId
  stageStatsKeys: {},
};

const getters = {
  getFunnels: $state => $state.records,
  getSelectedFunnel: $state => $state.selectedFunnel,
  getUIFlags: $state => $state.uiFlags,
  getFunnelById: state => id => {
    return state.records.find(funnel => String(funnel.id) === String(id));
  },
  getStageStats: state => funnelId => {
    return state.stageStats[funnelId] || {};
  },
};

const actions = {
  async fetch({ commit, dispatch, state }) {
    commit(types.SET_FUNNEL_UI_FLAG, { isFetching: true });
    try {
      const { data } = await FunnelAPI.get();
      const formattedData = data.map(funnel => ({
        ...funnel,
        id: String(funnel.id),
        stages: Object.entries(funnel.stages || {}).reduce(
          (acc, [key, stage]) => ({
            ...acc,
            [String(key)]: {
              ...stage,
              position: parseInt(stage.position, 10) || 0,
            },
          }),
          {}
        ),
      }));
      commit(types.SET_FUNNELS, formattedData);

      // --- Fallback centralizado ---
      let fallbackFunnel = null;
      if (formattedData.length > 0) {
        // Se já existe um selectedFunnel válido, usa a cópia vinda da API (dados frescos).
        // Não reutilizar state.selectedFunnel — ficaria desatualizado após update (ex.: stage_automations).
        const currentSelectedFunnelId = state.selectedFunnel ? String(state.selectedFunnel.id) : null;
        const freshSelected = formattedData.find(f => String(f.id) === currentSelectedFunnelId);
        if (currentSelectedFunnelId && freshSelected) {
          fallbackFunnel = freshSelected;
        } else {
          fallbackFunnel = formattedData[0];
        }
      }
      await dispatch('setSelectedFunnel', fallbackFunnel);
      // ---
      return formattedData;
    } catch (error) {
      console.error('Erro ao buscar funis:', error);
      // Em caso de erro, limpa o selectedFunnel
      await dispatch('setSelectedFunnel', null);
      throw error;
    } finally {
      commit(types.SET_FUNNEL_UI_FLAG, { isFetching: false });
    }
  },

  fetchFunnels(context) {
    return actions.fetch(context);
  },

  async setSelectedFunnel({ commit, state }, funnel) {
    if (typeof funnel === 'string' || typeof funnel === 'number') {
      funnel = state.records.find(
        record => String(record.id) === String(funnel)
      );
    }

    // Permitir que funnel seja null para desmarcar.
    // Se funnel for inválido, seta null.
    if (!funnel || typeof funnel.id === 'undefined') {
      commit(types.SET_SELECTED_FUNNEL, null);
      return;
    }
    try {
      commit(types.SET_FUNNEL_UI_FLAG, { isUpdating: true });
      commit(types.SET_SELECTED_FUNNEL, funnel); // funnel pode ser null
    } finally {
      commit(types.SET_FUNNEL_UI_FLAG, { isUpdating: false });
    }
  },

  async fetchStageStats(
    { commit, state },
    { funnelId, filterParams = {}, force = false }
  ) {
    const requestKey = `${funnelId}:${JSON.stringify(filterParams || {})}`;
    if (
      !force &&
      state.stageStatsKeys[funnelId] === requestKey &&
      state.stageStats[funnelId]
    ) {
      return state.stageStats[funnelId];
    }
    if (pendingStageStatsRequests[requestKey]) {
      return pendingStageStatsRequests[requestKey];
    }

    try {
      pendingStageStatsRequests[requestKey] = FunnelAPI.getStageStats(
        funnelId,
        filterParams
      ).then(({ data }) => {
        const stats = data.stages || data;
        commit('SET_STAGE_STATS', { funnelId, stats, requestKey });
        return stats;
      });
      return await pendingStageStatsRequests[requestKey];
    } catch (error) {
      console.error('Erro ao buscar stats das etapas:', error);
      commit('SET_STAGE_STATS', { funnelId, stats: {} });
      throw error;
    } finally {
      delete pendingStageStatsRequests[requestKey];
    }
  },
};

const mutations = {
  [types.SET_FUNNELS]($state, data) {
    $state.records = data;
    $state.stageStats = {};
    $state.stageStatsKeys = {};
  },
  [types.SET_SELECTED_FUNNEL]($state, funnel) {
    $state.selectedFunnel = funnel;
  },
  [types.SET_FUNNEL_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  SET_STAGE_STATS($state, { funnelId, stats, requestKey }) {
    $state.stageStats = {
      ...$state.stageStats,
      [funnelId]: stats,
    };
    if (!requestKey) return;

    $state.stageStatsKeys = {
      ...$state.stageStatsKeys,
      [funnelId]: requestKey,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
